import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../lib/i18n/context';

interface SEOHeadProps {
  title: string;
  description: string;
  image?: string;
  type?: string;
  article?: boolean;
  canonicalPath?: string;
}

export function SEOHead({
  title,
  description,
  image,
  type = 'website',
  article = false,
  canonicalPath
}: SEOHeadProps) {
  const location = useLocation();
  const { language } = useLanguage();
  
  // Determine canonical URL path, defaulting to current path if not specified
  const canonicalUrl = `https://abcbrain.xyz${canonicalPath || location.pathname}`;
  
  // Set default OG image based on language
  const defaultOgImage = language === 'de' 
    ? 'https://abcbrain.xyz/og-image-de.png'
    : 'https://abcbrain.xyz/og-image-en.png';
  
  // Use provided image or default
  const ogImage = image || defaultOgImage;

  return (
    <Helmet>
      {/* Basic tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />
      
      {/* OpenGraph tags */}
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content={article ? 'article' : type} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content={language === 'de' ? 'de_DE' : 'en_US'} />
      
      {/* Twitter tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      
      {/* Language */}
      <html lang={language} />
      
      {/* JSON-LD for better Google indexing */}
      <script type="application/ld+json">{`
        {
          "@context": "https://schema.org",
          "@type": "${article ? 'Article' : 'WebPage'}",
          "headline": "${title}",
          "description": "${description}",
          "image": "${ogImage}",
          "url": "${canonicalUrl}",
          "inLanguage": "${language === 'de' ? 'de-DE' : 'en-US'}"
        }
      `}</script>
    </Helmet>
  );
}